import parseISO from 'date-fns/parseISO';

import type { Asset, WatchHistory } from '../../../data/asset';
import { convertMediaTypeToRhinoAssetType } from '../../../data/compat/rhinoAssetTypeToMediaType';
import { tvpil } from '../../../data/compat/tvpil';
import type {
  WatchedAsset,
  WatchedEpisodeAsset,
  WatchedMovieAsset,
  WatchedProgramAsset,
  WatchedRecordingAsset,
} from '../../../data/continueWatching/types';
import { ICatalogItem, WatchedCatalogItem } from '../../objects';
import { fromKalturaBoolean } from '../../utils';
import { processMediaImages, processVodAssetMetas } from '../asset';

type WatchedHistory = Omit<WatchedCatalogItem, 'catalogItem'>;

export type RequiredWatchedCatalogItem = WatchedHistory & {
  catalogItem: NonNullable<WatchedCatalogItem['catalogItem']>;
};

const processors = {
  program: processProgram,
  movie: processMovie,
  episode: processEpisode,
};

export function processWatchedAsset(watchedCatalogItem: RequiredWatchedCatalogItem): WatchedAsset {
  const type = convertMediaTypeToRhinoAssetType(tvpil.dms.mediaTypesValue[watchedCatalogItem.catalogItem.type]);

  if (watchedCatalogItem.catalogItem.recording) {
    return processRecording(watchedCatalogItem.catalogItem, watchedCatalogItem);
  }

  const process = processors[type as keyof typeof processors] ?? processProgram;

  return process(watchedCatalogItem.catalogItem, watchedCatalogItem);
}

function processBaseAsset(catalogItem: ICatalogItem): Asset {
  return {
    id: catalogItem.id,
    title: catalogItem.name,
    ...(catalogItem.metadata?.['ParentalRating'] && {
      parentalRating: (catalogItem.metadata?.['ParentalRating'] as number[])?.[0],
    }),
    isAdult: fromKalturaBoolean(catalogItem.metadata?.['Adult']),
    type: convertMediaTypeToRhinoAssetType(tvpil.dms.mediaTypesValue[catalogItem.type]),
    images: processMediaImages(catalogItem.images),
  };
}

function processMosaic(catalogItem: ICatalogItem): Pick<WatchedProgramAsset, 'hasMosaic' | 'mosaicExternalId'> {
  const mosaicInfo = catalogItem.metadata?.['MosaicInfo'] as string[] | undefined;
  const isMosaicEvent = catalogItem.metadata?.['IsMosaicEvent'];

  return {
    hasMosaic: fromKalturaBoolean(isMosaicEvent),
    ...(mosaicInfo && { mosaicExternalId: mosaicInfo[0]?.split('=')[1] }),
  };
}

function processBookmark(bookmark: WatchedHistory): WatchHistory {
  return {
    position: bookmark.position,
    duration: bookmark.duration,
    hasFinishedWatching: bookmark.hasFinishedWatching,
  };
}

function processProgram(catalogItem: ICatalogItem, bookmark: WatchedHistory): WatchedProgramAsset {
  return {
    ...processBaseAsset(catalogItem),
    ...processBookmark(bookmark),
    ...processMosaic(catalogItem),
    channelId: catalogItem.linearChannelId!,
    ...(catalogItem.metadata?.['EpisodeName'] && {
      episodeName: String(catalogItem.metadata['EpisodeName']),
    }),
    ...(catalogItem.metadata?.['EpisodeNumber'] && {
      episodeNumber: Number(catalogItem.metadata['EpisodeNumber']),
    }),
    ...(catalogItem.metadata?.['SeasonNumber'] && {
      seasonNumber: Number(catalogItem.metadata['SeasonNumber']),
    }),
    ...(catalogItem.metadata?.['SeriesID'] && {
      seriesId: String(catalogItem.metadata['SeriesID']),
    }),
    endDate: catalogItem.endDate ? parseISO(catalogItem.endDate).getTime() : 0,
    startDate: catalogItem.startDate ? parseISO(catalogItem.startDate).getTime() : 0,
    type: 'program',
  };
}

function processMovie(catalogItem: ICatalogItem, bookmark: WatchedHistory): WatchedMovieAsset {
  return {
    ...processBaseAsset(catalogItem),
    ...processBookmark(bookmark),
    ...processVodAssetMetas(catalogItem.metadata),
    type: 'movie',
  };
}

function processEpisode(catalogItem: ICatalogItem, bookmark: WatchedHistory): WatchedEpisodeAsset {
  return {
    ...processBaseAsset(catalogItem),
    ...processBookmark(bookmark),
    ...processVodAssetMetas(catalogItem.metadata),
    ...(catalogItem.metadata?.['EpisodeName'] && {
      episodeName: String(catalogItem.metadata['EpisodeName']),
    }),
    ...(catalogItem.metadata?.['EpisodeNumber'] && {
      episodeNumber: Number(catalogItem.metadata['EpisodeNumber']),
    }),
    ...(catalogItem.metadata?.['SeasonNumber'] && {
      seasonNumber: Number(catalogItem.metadata['SeasonNumber']),
    }),
    ...(catalogItem.metadata?.['SeriesID'] && {
      seriesId: String(catalogItem.metadata['SeriesID']),
    }),
    type: 'episode',
  };
}

function processRecording(catalogItem: ICatalogItem, bookmark: WatchedHistory): WatchedRecordingAsset {
  return {
    ...processBaseAsset(catalogItem),
    ...processBookmark(bookmark),
    ...processMosaic(catalogItem),
    channelId: catalogItem.linearChannelId!,
    ...(catalogItem.metadata?.['EpisodeName'] && {
      episodeName: String(catalogItem.metadata['EpisodeName']),
    }),
    ...(catalogItem.metadata?.['EpisodeNumber'] && {
      episodeNumber: Number(catalogItem.metadata['EpisodeNumber']),
    }),
    ...(catalogItem.metadata?.['SeasonNumber'] && {
      seasonNumber: Number(catalogItem.metadata['SeasonNumber']),
    }),
    ...(catalogItem.metadata?.['SeriesID'] && {
      seriesId: String(catalogItem.metadata['SeriesID']),
    }),
    endDate: catalogItem.endDate ? parseISO(catalogItem.endDate).getTime() : 0,
    recordingId: catalogItem.recording?.id,
    startDate: catalogItem.startDate ? parseISO(catalogItem.startDate).getTime() : 0,
    type: 'recording',
  };
}
